import { CACHE_TAGS } from '@services/http/http';
import {
    getLocalVariable,
    getSessionVariable,
    removeSessionVariable,
    setLocalVariable,
    setSessionVariable,
} from '@services/storage';

import {
    GALIUM_DATA_STORAGE_KEY,
    GALLIUM_PRICES_STORAGE_KEY,
    GALLIUM_STATUS_STORAGE_KEY,
    GalliumDataStoreApi,
    GalliumOffreApi,
    GalliumPrice,
    GalliumPriceArray,
    GalliumType,
    OffreCibleTypeApi,
    OffrePrincipaleDetenueTypeApi,
    OffresSecondairesTypeApi,
    RefundType,
    SimulateDataTypeApi,
} from '@app-types/renewal';
import { ACTION_TYPES } from '@app-types/simulator/simulator';
import { UserType } from '@app-types/user';

import { getAppConfig } from './config';
import { apiHttpService } from './http';

const appConfig = getAppConfig();

export async function getSimulateData(contractId: string, user: UserType): Promise<GalliumPriceArray | undefined> {
    const galliumDataItem = getSessionVariable(GALIUM_DATA_STORAGE_KEY);
    const galliumData: GalliumType = galliumDataItem ? JSON.parse(galliumDataItem) : null;
    const shouldGetDataFromStorage = !!(galliumData && galliumData[contractId]);
    if (shouldGetDataFromStorage) {
        return JSON.parse(getLocalVariable(GALLIUM_PRICES_STORAGE_KEY) || '{}');
    } else {
        try {
            const data = await getSimulateDataFromApi(contractId, user);
            if (data) {
                return populateGalliumData(data, contractId);
            }
        } catch {}
    }
}

export async function getSimulateDataFromApi(
    contractId: string,
    user: UserType,
): Promise<GalliumDataStoreApi | undefined> {
    try {
        const data = await apiHttpService.get<SimulateDataTypeApi>(
            `${appConfig.refcli.url}/contrats/${contractId}/simulation-impacts-offres?typeParcours=RENOUVELLEMENT`,
            undefined,
            { user, ttl: parseInt(getAppConfig().cache.ttl.productDetail), tags: [CACHE_TAGS.PRODUCT_DETAIL] },
        );

        return transformData(data);
    } catch {}
}

function transformData(simulatedData: SimulateDataTypeApi): GalliumDataStoreApi {
    const ops = simulatedData.simulations.map((el) => el.offrePrincipaleCible.idOffre);
    const offrePrincipaleDetenue = simulatedData.offrePrincipaleDetenue;
    const offresSecondairesDetenues = simulatedData.offresSecondairesDetenues;
    const simulation = simulatedData.simulations.map((simulation) =>
        formatOffer(simulation.offrePrincipaleCible, simulation.offresSecondairesCibles),
    );
    const opSource = formatOffer(offrePrincipaleDetenue, offresSecondairesDetenues);
    return { ops, opSource, simulation };
}

function formatOffer(offer: OffrePrincipaleDetenueTypeApi, osDetenuesList: OffresSecondairesTypeApi): GalliumOffreApi {
    const formatedOffre = formatOfferDetenues(offer);
    const filteredOffreAvantage = osDetenuesList?.filter(
        (os) => os.typeOffre === 'AVANTAGE' && os.action !== ACTION_TYPES.RESILIATION,
    );
    const remiseAvantage = filteredOffreAvantage?.map((el) => formatOfferDetenues(el));
    return { ...formatedOffre, remiseAvantage };
}

function formatOfferDetenues(offre: Partial<OffreCibleTypeApi>): GalliumOffreApi {
    return {
        idOffre: offre.idOffre,
        libelleCommercial: offre.libelleCommercial,
        libelleSysteme: offre.libelleCommercial,
        familleVente: offre.famillesVente,
        tarifRecurrent: {
            tarifHT: offre.tarifHT,
            tarifTTC: offre.tarifTTC,
            tauxTVA: offre.tauxTVA,
        },
        tarifMoinsRemise: {
            tarifTTC: offre.tarifRemiseTTC,
            montantRemiseTTC: offre.montantRemiseTTC,
        },
        dureeOffre: offre.dureeApplication ? offre.dureeApplication.duree : 0,
        uniteDureeOffre: offre.dureeApplication ? offre.dureeApplication.uniteDuree : '',
        dateFinValidite: offre.dateFinValidite,
        // remiseAvantage: offre.
    };
}

function populateGalliumData(data: GalliumDataStoreApi, key: string): GalliumPriceArray | undefined {
    const dataToStore: GalliumType = {};
    dataToStore[key] = data;
    setSessionVariable(GALIUM_DATA_STORAGE_KEY, JSON.stringify(dataToStore));
    const prices = managePrices(dataToStore[key]);
    setLocalVariable(GALLIUM_PRICES_STORAGE_KEY, JSON.stringify(prices));
    removeSessionVariable(GALLIUM_STATUS_STORAGE_KEY);
    return prices;
}

function managePrices(data?: GalliumDataStoreApi): GalliumPriceArray | undefined {
    const pricesList: { [key: string]: GalliumPrice } = {};
    const idOffre = data?.opSource?.idOffre;

    if (!idOffre) {
        return;
    }

    const formattedPriceItem = formatPriceItem(data.opSource);

    if (formattedPriceItem) {
        pricesList[idOffre] = formattedPriceItem;
    }

    for (const offer of data.simulation) {
        if (!(offer.message && offer.message[0] && offer.message[0].type === 'ERROR')) {
            const formattedPrice = formatPriceItem(offer);
            const idOffre = offer.idOffre;
            if (formattedPrice && idOffre) {
                pricesList[idOffre] = formattedPrice;
            }
        }
    }
    return pricesList;
}

function formatPriceItem(data: GalliumOffreApi): GalliumPrice | null {
    if (data.message && data.message[0] && data.message[0].type === 'ERROR') {
        return null;
    }

    const prices: GalliumPrice = {
        price: data.tarifRecurrent?.tarifTTC,
    };

    if (
        data.tarifMoinsRemise &&
        data.tarifMoinsRemise.montantRemiseTTC &&
        data.tarifRecurrent?.tarifTTC &&
        data.tarifMoinsRemise.montantRemiseTTC < 0 &&
        data.tarifRecurrent.tarifTTC !== data.tarifMoinsRemise.tarifTTC
    ) {
        prices.promoPrice = data.tarifMoinsRemise.tarifTTC;
        prices.refund = data.tarifMoinsRemise.montantRemiseTTC;

        let arrRefunds: RefundType[] = [];
        let refundsWithDate = 0;

        if (data.remiseAvantage) {
            arrRefunds = data.remiseAvantage.map((remiseAvantage) => {
                const refund: RefundType = {
                    untilDate: remiseAvantage.dateFinValidite ?? 0,
                    name: remiseAvantage.libelleCommercial,
                    amount: remiseAvantage.tarifRecurrent?.tarifTTC ?? 0,
                    duration: remiseAvantage.dureeOffre ?? 0,
                };

                if (refund.duration) {
                    refundsWithDate++;
                    prices.promoLabel = `pendant ${refund.duration} ${remiseAvantage.uniteDureeOffre?.toLowerCase()} `;
                }

                return refund;
            });
        }

        if (arrRefunds.length) {
            prices.refundsList = arrRefunds;
            prices.refundsWithDate = refundsWithDate;
        }
    }
    return prices;
}
